import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Download, Printer } from "lucide-react";
import api from "../services/api";

// The bill arrives as HTML already filled in on the server from the distributor's print format
// (sales_invoice_print); values are escaped there, so it is safe to drop into the page as is.
const InvoiceContent = ({ data }) => (
  <>
    {!data.sellerProfileComplete ? (
      <div className="error no-print" style={{ marginBottom: "12px" }}>
        Your company profile isn't set up yet, so the seller details below are blank. Click your name in the top-right corner to add them.
      </div>
    ) : null}
    <div dangerouslySetInnerHTML={{ __html: data.html }} />
  </>
);

/**
 * Save as PDF without a PDF library: the browser's print dialog with "Save as PDF" as the destination. The page title is set to
 * the invoice number for the moment of printing, so the suggested file name is e.g. "INV-0001.pdf".
 */
const saveAsPdf = (invoiceNumber) => {
  const previousTitle = document.title;
  document.title = (invoiceNumber || "invoice").replace(/[\\/:*?"<>|]+/g, "-");
  toast.info('In the print dialog, choose "Save as PDF" as the destination.', { autoClose: 6000 });
  const restore = () => {
    document.title = previousTitle;
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  // let the toast render before the dialog blocks the page
  setTimeout(() => window.print(), 150);
};

/** Width of the bill on A4 portrait: the page less the 12 mm margin each side. */
const PRINT_WIDTH = "186mm";

/**
 * How much the bill has to shrink to fit the width of an A4 portrait page (1 = it fits): it is laid out off screen at that width and
 * the right edge of its widest part is measured.
 */
const printScale = (html) => {
  const probe = document.createElement("div");
  probe.style.cssText = `position:absolute;left:-10000px;top:0;width:${PRINT_WIDTH};visibility:hidden`;
  probe.innerHTML = html;
  document.body.appendChild(probe);
  try {
    const left = probe.getBoundingClientRect().left;
    // elements with no box (style tags, hidden parts) do not count
    const widest = Math.max(
      probe.clientWidth,
      ...Array.from(probe.querySelectorAll("*"), (el) => {
        const box = el.getBoundingClientRect();
        return box.width || box.height ? box.right - left : 0;
      })
    );
    return widest > probe.clientWidth ? Math.max(0.5, probe.clientWidth / widest) : 1;
  } finally {
    probe.remove();
  }
};

const InvoicePrintModal = ({ invoiceId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (data?.html) setScale(printScale(data.html));
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await api.get(`/api/v1/sales-invoices/${invoiceId}/print`);
        if (!cancelled) setData(result);
      } catch (error) {
        toast.error(error.message || "Failed to load invoice");
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  return (
    <>
      <div className="modal-overlay no-print" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="modal-box" style={{ maxWidth: "900px" }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Print Invoice</h3>
            <div className="flex gap-2">
              {data ? (
                <button className="primary" onClick={() => window.print()} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <Printer size={15} /> Print
                </button>
              ) : null}
              {data ? (
                <button
                  className="secondary"
                  onClick={() => saveAsPdf(data.invoiceNumber)}
                  style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                  title="Save this invoice as a PDF file"
                >
                  <Download size={15} /> Save PDF
                </button>
              ) : null}
              <button className="close-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </div>

          {loading || !data ? (
            <div className="empty-state">
              <span className="empty-state-title">Loading invoice...</span>
            </div>
          ) : (
            <InvoiceContent data={data} />
          )}
        </div>
      </div>

      {/* Rendered directly into document.body so it escapes the modal's flex/absolute layout entirely
          when printed — nesting the printable area inside the on-screen modal caused it to render
          far down a blank page instead of at the top. */}
      {data ? createPortal(
        <div className="print-portal">
          {/* The page itself is printed with no margin, which is what stops the browser adding its own
              header (date, page title) and footer (URL). The empty header/footer rows of this table
              repeat on every sheet and give the bill its margin back. */}
          <table className="print-frame">
            <thead>
              <tr>
                <td />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="print-fit" style={{ "--print-scale": scale }}>
                    <InvoiceContent data={data} />
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>,
        document.body
      ) : null}
    </>
  );
};

export default InvoicePrintModal;
